import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Group,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import { useSession } from '../../app/session';
import { PageHeader } from '../../components/PageHeader';
import { LANGUAGES, djangoTagFor, normaliseLanguage, setLanguage } from '../../i18n';
import { applyFieldErrors, notifyError, notifyInfo, notifySaved } from '../../lib/notify';

export function ProfilePage() {
  const { t } = useTranslation();
  const { user, organisation, organisations, refresh } = useSession();
  const [language, setPreferred] = useState(() => normaliseLanguage(user?.preferred_language));

  const passwordForm = useForm({
    initialValues: { current_password: '', new_password: '', confirm: '' },
    validate: {
      current_password: (value) => (value ? null : t('common.requiredField')),
      new_password: (value) => (value.length >= 10 ? null : t('auth.passwordTooShort')),
      confirm: (value, values) =>
        value === values.new_password ? null : t('auth.passwordMismatch'),
    },
  });

  const changePassword = useMutation({
    mutationFn: async (values: typeof passwordForm.values) =>
      unwrap(
        await api.POST('/api/v1/auth/password/', {
          body: {
            current_password: values.current_password,
            new_password: values.new_password,
          },
        }),
      ),
    onSuccess: () => {
      notifyInfo(t('auth.passwordChanged'));
      passwordForm.reset();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, passwordForm.setErrors)) notifyError(error);
    },
  });

  const savePreferences = useMutation({
    mutationFn: async (value: string) =>
      unwrap(
        await api.PATCH('/api/v1/auth/me/', {
          body: { preferred_language: djangoTagFor(value) },
        }),
      ),
    onSuccess: async (_data, value) => {
      notifySaved();
      await setLanguage(value);
      await refresh();
    },
    onError: notifyError,
  });

  if (!user) return null;

  return (
    <>
      <PageHeader title={t('settings.myProfile')} subtitle={user.email} />

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card p="md">
          <Text fw={600} mb="sm">
            {t('staff.person')}
          </Text>
          <Table fz="sm" withRowBorders={false} verticalSpacing={4}>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td c="dimmed" w={160}>
                  {t('common.name')}
                </Table.Td>
                <Table.Td>{user.person?.full_name ?? user.full_name}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td c="dimmed">{t('staff.email')}</Table.Td>
                <Table.Td>{user.email}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td c="dimmed">{t('common.organisation')}</Table.Td>
                <Table.Td>{organisation?.name}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          <Text fz="sm" fw={600} mt="md" mb="xs">
            {t('common.switchOrganisation')}
          </Text>
          <Stack gap="xs">
            {organisations.map((entry) => (
              <Group key={entry.id} justify="space-between">
                <Text fz="sm">{entry.name}</Text>
                <Group gap={4}>
                  {entry.roles.map((role) => (
                    <Badge key={role} size="xs" variant="light">
                      {role}
                    </Badge>
                  ))}
                </Group>
              </Group>
            ))}
          </Stack>
        </Card>

        <Stack gap="md">
          <Card p="md">
            <Text fw={600} mb="sm">
              {t('settings.preferences')}
            </Text>
            <Group align="flex-end" gap="sm">
              <Select
                label={t('common.language')}
                data={LANGUAGES.map((entry) => ({
                  value: entry.key,
                  label: `${entry.flag} ${entry.label}`,
                }))}
                value={language}
                onChange={(value) => value && setPreferred(normaliseLanguage(value))}
                allowDeselect={false}
                w={200}
              />
              <Button
                loading={savePreferences.isPending}
                onClick={() => savePreferences.mutate(language)}
              >
                {t('action.save')}
              </Button>
            </Group>
          </Card>

          <Card p="md">
            <Text fw={600} mb="sm">
              {t('auth.changePassword')}
            </Text>
            <form
              onSubmit={passwordForm.onSubmit((values) => changePassword.mutate(values))}
            >
              <Stack gap="sm">
                <PasswordInput
                  label={t('auth.currentPassword')}
                  required
                  {...passwordForm.getInputProps('current_password')}
                />
                <PasswordInput
                  label={t('auth.newPassword')}
                  required
                  {...passwordForm.getInputProps('new_password')}
                />
                <PasswordInput
                  label={t('auth.newPassword')}
                  required
                  {...passwordForm.getInputProps('confirm')}
                />
                <Button type="submit" loading={changePassword.isPending}>
                  {t('auth.changePassword')}
                </Button>
              </Stack>
            </form>
          </Card>
        </Stack>
      </SimpleGrid>
    </>
  );
}
